'use client'
import AppliedFilters from '@/app/components/AppliedFilters'
import Button from '@/app/components/common/Button'
import Text from '@/app/components/common/Text'
import Title from '@/app/components/common/Title'
import FiltersPlaceholder from '@/app/components/FiltersPlaceholder'
import { HotelsListMap } from '@/app/components/HotelsListMap'
import HotelsPagination from '@/app/components/HotelsPaginatioin'
import ResultsFilters from '@/app/components/ResultsFilters'
import SortingAndFilters from '@/app/components/SortingAndFilters'
import { Icons } from '@/app/components/ui/Icons'
import {
	DEFAULT_CHECK_IN_DATE,
	DEFAULT_CHECK_OUT_DATE,
	DEFAULT_ROOMS_AND_GUESTS,
	EMPTY_HOTELS_RESPONSE,
	TABLET_DOWN
} from '@/app/constants'
import eventBus from '@/app/eventBus'
import {
	emitListViewChangeGAEvent,
	emitRpNoHotelsFoundFiltersGAEvent,
	emitRpNoHotelsFoundGAEvent,
	emitRpNoSpecificHotelGAEvent,
	emitRpResetAllFiltersGAEvent,
	emitRpSearchFailedGAEvent
} from '@/app/ga-event-helpers'
import { useFallbackHotelsQuery } from '@/app/hooks/queries/useFallbackHotelsQuery'
import { useHotelsQuery } from '@/app/hooks/queries/useHotelsQuery'
import { useSimilarHotelsQuery } from '@/app/hooks/queries/useSimilarHotelsQuery'
import useIsGAReady from '@/app/hooks/useIsGAReady'
import { useMapView } from '@/app/hooks/useMapView'
import { useUserData } from '@/app/hooks/useUserData'
import { PS_EVENTS } from '@/app/ps-events'
import {
	DestPagesProps,
	FiltersCountMap,
	HotelsListResponse,
} from '@/app/types'
import {
	capitalize,
	determineSearchPlaceFromPath,
	getAppliedFiltersFromURL,
	isAirport,
	isHood,
	isLandmark,
	parseSearchParamsToLower
} from '@/app/utils'
import { useMediaQuery } from '@uidotdev/usehooks'
import classNames from 'classnames'
import { camelCase, startCase } from 'lodash'
import { useTranslations } from 'next-intl'
import { useRouter, useSearchParams } from 'next/navigation'
import { useEffect, useMemo, useRef, useState } from 'react'
import HotelsList from './HotelsList'

export interface Props extends DestPagesProps {
}

/**
 * Hotels Component
 *
 * Error Handling Strategy:
 * 1. useUserData hook handles localStorage errors gracefully with fallback data
 * 2. Component shows loading state while userData is being loaded
 * 3. If userData fails to load, a warning is logged but the app continues with fallback data
 * 4. The parent component (HotelsWithErrorBoundary) catches any unexpected errors
 *
 * This ensures the app remains functional even if:
 * - localStorage is blocked (private browsing)
 * - userData is corrupted
 * - localStorage quota is exceeded
 */
const Hotels = (props: Props) => {
	const { dest, lang } = props.params
	const [country, region, city, hotel, place] = dest
	const {
		locationName,
		s,
		gr,
		p,
		fa,
		t: themesParam,
		mx,
		mn,
		lmx,
		lmn,
		page,
		polygon,
		lat,
		lng
	} = props.searchParams
	const fallbackLocation = dest[dest.length - 1]
	const displayLocation = locationName && locationName !== 'undefined'
		? decodeURIComponent(locationName as string)
		: startCase(camelCase(decodeURIComponent(fallbackLocation)))
	const {
		userData,
		isLoading: isUserDataLoading,
		error: userDataError,
	} = useUserData()

	const router = useRouter()
	const queryParams = useSearchParams()
	const t = useTranslations('Keywords')
	const isGAReady = useIsGAReady()
	const { isListView, isFullScreenMap } = useMapView();
	const startTimeRef = useRef<number>(Date.now())
	const [hotelsState, setHotelsState] = useState<HotelsListResponse | null>(
		null
	)
	const [mapQueryKey, setMapQueryKey] = useState<string>('')
	const [noHotelsFound, setNoHotelsFound] = useState(false)
	const [shouldRunFallbackQuery, setShouldRunFallbackQuery] = useState(false)
	const appliedFilters = getAppliedFiltersFromURL(queryParams)
	const isSingleHotelRequest = Boolean(props.searchParams.hotelID)
	const prevDestRef = useRef<string>('')
	const prevSearchParamsRef = useRef<string>('')
	// AM-123 (H2): per-search idempotency latches. The no-results/no-availability
	// emit effect re-runs on every react-query poll tick (deps include `hotelsData`),
	// so without these it re-fired the same event up to ~100× per search. Each latch
	// holds the search key it last emitted for; a new search resets it.
	const noResultsEmittedKeyRef = useRef<string>('')
	const noSpecificHotelEmittedKeyRef = useRef<string>('')
	const isLandmarkSearch = isLandmark(hotel)
	const tabletDown = useMediaQuery(TABLET_DOWN)

	const [hoveredItem, setHoveredItem] = useState<null | number | string>(null)

	const hasFilters = !!(s || gr || p || fa || themesParam || mx || mn)
	const shouldFetchSimilarData =
		!!city && !isAirport(city) && !!hotel && !isLandmarkSearch && !isHood(hotel)

	const [isFetchingComplete, setIsFetchingComplete] = useState(false)
	const [isFallbackFetchingComplete, setIsFallbackFetchingComplete] =
		useState(false)
	const [isSimilarFetchingComplete, setIsSimilarFetchingComplete] =
		useState(false)
	const [useSimilarStarsFilter, setUseSimilarStarsFilter] = useState(true)
	const searchParams = parseSearchParamsToLower(props.searchParams)
	const placeID = searchParams?.['placeid']
	const hotelID = searchParams?.['hotelid']

	const renderTitle = () => {
		const resultsCount = hotelsData?.pages[0]?.totalResults || 0;
		return t(isLandmarkSearch ? 'bestHotels' : 'bestHotelsIn',
			{
				resultsCount,
				displayLocation: capitalize(displayLocation)
			})
	}

	const normalizedAvailableFilters = useMemo<FiltersCountMap>(() => {
		const base: FiltersCountMap = {
			facilities: {},
			guestRatings: {},
			hotelThemes: {},
			propertyTypes: {},
			starRatings: {},
		}

		const apiFilters = (hotelsState?.filters || {}) as Partial<FiltersCountMap>
		const total = hotelsState?.totalResults ?? 0
		base.hotelThemes = apiFilters.hotelThemes || {}
		if (apiFilters.propertyTypes) {
			base.propertyTypes = apiFilters.propertyTypes;
		}
		base.facilities = Object.fromEntries(
			Object.entries(apiFilters.facilities || {}).map((value) => [
				value[0],
				value[1] > Number(total) ? Number(total) : value[1],
			])
		)
		base.starRatings = Object.fromEntries(
			Object.entries(apiFilters.starRatings || {}).map((value) => [
				value[0],
				value[1] > Number(total) ? Number(total) : value[1],
			])
		)
		base.guestRatings = Object.fromEntries(
			Object.entries(apiFilters.guestRatings || {}).map((value) => [
				value[0],
				value[1] > Number(total) ? Number(total) : value[1],
			])
		)
		return base

	}, [fa, gr, hotelsState?.filters, hotelsState?.totalResults, p, s])

	useEffect(() => {
		const currentDest = props.params.dest.join(',')
		const currentSearchParams = JSON.stringify(props.searchParams)

		if (
			prevDestRef.current !== currentDest ||
			prevSearchParamsRef.current !== currentSearchParams ||
			(mapQueryKey && mapQueryKey !== props.searchParams.polygon)
		) {
			startTimeRef.current = Date.now()
			prevDestRef.current = currentDest
			prevSearchParamsRef.current = currentSearchParams
		}
	}, [props.params.dest, props.searchParams, mapQueryKey])

	const {
		data: hotelsData,
		isLoading: isLoadingHotels,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
	} = useHotelsQuery({
		props,
		startTimeRef,
		setIsFetchingComplete,
		userData,
		mapQueryKey,
	})

	useEffect(() => {
		const currentSearchParams = new URLSearchParams(queryParams.toString());

		if ((isListView || (!isFullScreenMap && tabletDown)) && (polygon || currentSearchParams.has('polygon'))) {

			// Delete the specified parameter
			currentSearchParams.delete('polygon');
			// currentSearchParams.delete('lat');
			// currentSearchParams.delete('lng');
			currentSearchParams.delete('page');

			// Construct the new URL path
			const newPathname = window.location.pathname; // Get the current path without query params
			const newQueryString = currentSearchParams.toString();
			const newUrl = newQueryString ? `${newPathname}?${newQueryString}` : newPathname;
			setMapQueryKey('');
			// Update the URL using router.replace to avoid adding a new entry to the history
			router.replace(newUrl, { scroll: false }); // s
		}
	}, [isListView, tabletDown, isFullScreenMap])

	useEffect(() => {
		if (hotelsData?.pages && hotelsData.pages.length > 0) {
			const lastPage = hotelsData.pages[hotelsData.pages.length - 1]
			const firstPage = hotelsData.pages[0];
			const singleHotelNoAvailability =
				isSingleHotelRequest &&
				hotelsData.pages[0]?.hotels[0]?.rawHotelData?.bestRate.availableRooms === 0
			setHotelsState(lastPage || null)

			const placeType = determineSearchPlaceFromPath(props.params.dest, false)

			const dates = {
				'check-in-date':
					props.searchParams['check-in-date'] || DEFAULT_CHECK_IN_DATE,
				'check-out-date':
					props.searchParams['check-out-date'] || DEFAULT_CHECK_OUT_DATE,
			}
			const room = props.searchParams.room || DEFAULT_ROOMS_AND_GUESTS
			const eventParams = {
				...props.searchParams,
				...dates,
				room,
				loctype: placeType.type,
			}

			// AM-123 (H2): only emit once the search has settled (poll complete or
			// failed) and only once per search key — the effect re-runs on every
			// ~600ms poll tick, so the latch is what prevents the re-fire storm.
			// The key includes mapQueryKey (matching BHP) so a map-polygon re-search
			// with no results still re-emits.
			const searchKey = `${props.params.dest.join(',')}|${JSON.stringify(
				props.searchParams
			)}|${mapQueryKey}`
			const isSettled =
				isFetchingComplete ||
				!!firstPage?.complete ||
				!!firstPage?.requestFailed

			if (
				noHotelsFound &&
				isGAReady &&
				!isSingleHotelRequest &&
				isSettled &&
				noResultsEmittedKeyRef.current !== searchKey
			) {
				noResultsEmittedKeyRef.current = searchKey
				if (firstPage.requestFailed) {
					emitRpSearchFailedGAEvent(eventParams)
				} else if (hasFilters) {
					emitRpNoHotelsFoundFiltersGAEvent(eventParams)
				} else {
					emitRpNoHotelsFoundGAEvent(eventParams)
				}
			}

			if (
				singleHotelNoAvailability &&
				isGAReady &&
				isSettled &&
				noSpecificHotelEmittedKeyRef.current !== searchKey
			) {
				noSpecificHotelEmittedKeyRef.current = searchKey
				emitRpNoSpecificHotelGAEvent(eventParams)
			}
		}
	}, [
		hotelsData,
		hasFilters,
		isGAReady,
		isSingleHotelRequest,
		noHotelsFound,
		isFetchingComplete,
		mapQueryKey,
	])

	const data: HotelsListResponse = hotelsState || EMPTY_HOTELS_RESPONSE

	const {
		data: similarHotelsData,
		isLoading: similarHotelsLoading,
		fetchNextPage: fetchNextSimilarPage,
		hasNextPage: hasNextSimilarPage,
		isFetchingNextPage: isFetchingNextSimilarPage,
		isSuccess: isSimilarSuccess,
	} = useSimilarHotelsQuery({
		props,
		country,
		region,
		city,
		s,
		hotelID,
		placeID,
		useSimilarStarsFilter,
		shouldFetchSimilarData,
		data,
		startTimeRef,
		setIsSimilarFetchingComplete,
		userData,
		mapQueryKey,
	})

	const similarData = similarHotelsData?.pages[0] || null
	useEffect(() => {
		if (
			isSimilarSuccess &&
			useSimilarStarsFilter &&
			similarHotelsData?.pages &&
			similarHotelsData.pages[0]?.hotels.length === 0 &&
			isSimilarFetchingComplete
		) {
			setUseSimilarStarsFilter(false)
		}
	}, [
		isSimilarSuccess,
		similarHotelsData,
		useSimilarStarsFilter,
		isSimilarFetchingComplete,
	])

	const {
		data: fallbackHotelsData,
		isLoading: isFallbackLoading,
		fetchNextPage: fetchNextFallbackPage,
		hasNextPage: hasNextFallbackPage,
		isFetchingNextPage: isFetchingNextFallbackPage,
	} = useFallbackHotelsQuery({
		props,
		shouldRunFallbackQuery,
		startTimeRef,
		setIsFallbackFetchingComplete,
		userData,
		mapQueryKey,
	})

	const isLoadingAnyData = isLoadingHotels || isFallbackLoading || similarHotelsLoading;
	useEffect(() => {
		if (hotelsData?.pages && hotelsData.pages.length > 0) {
			const lastPage = hotelsData.pages[hotelsData.pages.length - 1]
			setHotelsState(lastPage || null)
			// Check if we should run the fallback query
			if (lastPage && lastPage.hotels.length === 0 && !isLoadingAnyData && hotelsData?.pages[0]?.complete) {
				setNoHotelsFound(true)
				setShouldRunFallbackQuery(!isSingleHotelRequest)
			} else {
				setNoHotelsFound(false)
				setShouldRunFallbackQuery(false)
			}
		}
	}, [hotelsData, hasFilters, isLoadingAnyData])

	const handleResetFilters = () => {
		emitRpResetAllFiltersGAEvent()

		const { s, gr, fa, p, t, mn, mx, lmn, lmx, page, ...restParams } = props.searchParams

		const searchParams = new URLSearchParams(
			Object.fromEntries(
				Object.entries(restParams).map(([key, value]) => [
					key,
					Array.isArray(value) ? value.join(',') : value,
				])
			)
		)

		const newUrl = `${window.location.pathname}?${searchParams.toString()}`
		router.push(newUrl)
	}

	const minRate = lmn ?
		Math.min(data.lowestNightlyRate === 0
			? lmn
			: data.lowestNightlyRate, lmn) : data.lowestNightlyRate
	const maxRate = lmx
		? Math.max(data.highestNightlyRate, lmx)
		: data.highestNightlyRate;

	// Keeping and expanding maximum and minimum price rates when moving the map and updating the filters
	const [lowestNightlyRate, setLowestNightlyRate] = useState(minRate)
	const [highestNightlyRate, setHighestNightlyRate] = useState(maxRate)
	if (minRate !== 0 && (minRate < lowestNightlyRate || lowestNightlyRate === 0))
		setLowestNightlyRate(minRate)
	if (maxRate !== 0 && (maxRate > highestNightlyRate || highestNightlyRate === 0))
		setHighestNightlyRate(maxRate)
	const availableRates = useMemo(() => ({ min: lowestNightlyRate, max: highestNightlyRate, }), [highestNightlyRate, lowestNightlyRate])

	const NoHotelsFound = () => (
		<div className={classNames("flex items-start p-4 md:mb-4 lg:mb-6 xl:mb-8 bg-text-200 max-md:-mx-4 md:p-6 lg:bg-text-100 md:rounded-4xl",
			{ "xl:flex-col": !isListView }
		)}>
			<Icons.iconAlert className={classNames("me-2 max-xl:hidden", { "mb-2": !isListView })} />
			<div className={classNames("flex flex-col text-text-900 gap-1", { "gap-2": !isListView })}>
				<span className="font-bold leading-5 xl:text-xl lg:leading-6">
					{t('hotelsSearch404', { location: displayLocation })}
				</span>
				<span className="leading-5 max-xl:text-sm">
					{t('tryChangeDates')}
				</span>
			</div>
		</div>
	)

	const ContentHeader = () => {
		const hasAvailability = isSingleHotelRequest && data.hotels[0]?.rawHotelData.bestRate.availableRooms > 0;
		return (
			<div
				className="flex-shrink-0 flex items-start max-md:items-center flex-1 basis-0 min-w-0 max-md:mt-2 max-sm:mt-0"
				data-test-id="results-title"
			>
				{isPageLoadingWithData || isLoadingHotels ? (
					<div className="hotels-list-placeholder__count" />
				) : (
					!isSingleHotelRequest ? (
						<h2 className="font-bold text-sm lg:text-lg text-text-900 truncate pr-3 rtl:pr-0 rtl:pl-3">
							{noHotelsFound ? (
								isListView ? (
									t('dealsThatYouMightLike')
								) : ''
							) : renderTitle()}
						</h2>
					) : (
						<h2 className="font-bold text-sm lg:text-lg text-text-900 pr-3 rtl:pr-0 rtl:pl-3">
							{isListView ? (
								<>
									{t('hotelsSimilarTo')}
									<span className="mx-1">
										{decodeURIComponent(hotel)}
									</span>
								</>
							) : hasAvailability ? t('roomsAreAvailable') : t('noRoomsTryDates')}
						</h2>
					)
				)}
			</div>
		)
	}
	const NoResultsFound = ({ isPageLoading }: { isPageLoading: boolean }) =>
		!data.hotels.length && hasFilters && !(isPageLoading || isLoadingHotels) && (
			<>
				<div className="bg-text-200 lg:bg-text-200 max-lg:-ms-4 max-lg:-me-4 lg:rounded-4xl p-4 lg:p-6 mb-6">
					<div className="mb-4">
						<Title level="4" size="base" bold>
							{t('hotelsFound', {
								count: data.hotels?.length || 0,
								location: locationName as string,
							})}
						</Title>
						<Text className="text-text-900 text-sm">{t('tryToReset')}</Text>
					</div>
					<div>
						<AppliedFilters
							appliedFilters={{
								...appliedFilters,
								// Price range filter logic:
								// If both min and max are 0, return undefined (no filter applied)
								// If min is 0, use data.lowestNightlyRate; otherwise, use the applied min
								// If max is 0, use data.highestNightlyRate; otherwise, use the applied max
								priceRange:
									appliedFilters.priceRange[0] === 0 &&
										appliedFilters.priceRange[1] === 0
										? undefined
										: [
											appliedFilters.priceRange[0] === 0
												? lowestNightlyRate
												: appliedFilters.priceRange[0],
											appliedFilters.priceRange[1] === 0
												? highestNightlyRate
												: appliedFilters.priceRange[1],
										],
							}}
							searchParams={queryParams}
						/>
						<button
							data-test-id="reset-filters-button"
							onClick={handleResetFilters}
							className={classNames(
								'px-4 py-2 rounded-full flex items-center w-fit bg-white border text-score-good font-bold border-score-good hover:bg-text-200 transition-colors'
							)}
						>
							{t('resetAllFilters')}
						</button>
					</div>
				</div>
			</>
		)

	const handleChangeListViewLG = () => {
		emitListViewChangeGAEvent(isFullScreenMap)
		eventBus.publish(PS_EVENTS.FULL_SCREEN_MAP, !isFullScreenMap)
	}
	// Show loading state while userData is loading or hotels are loading
	const isPageLoadingWithData = isUserDataLoading ||
		!userData ||
		(isLoadingHotels && !data.hotels.length) ||
		(similarHotelsLoading && !similarData?.hotels.length) ||
		//@ts-ignore
		(isFallbackLoading && !fallbackHotelsData?.pages)
	const shouldShowMap = (!isListView && !tabletDown) || isFullScreenMap;
	// Show error message if userData failed to load but continue with fallback data
	if (userDataError) {
		console.warn('Failed to load user data, using fallback:', userDataError)
	}
	const hotels = hotelsData
		? hotelsData.pages.flatMap((page) => page.hotels)
		: data.hotels

	const _lat = lat ? Number(lat) : 0;
	const _lng = lng ? Number(lng) : 0;
	const mapCenter = { lat: _lat, lng: _lng };
	const mapHotelsArray = shouldRunFallbackQuery && fallbackHotelsData ? fallbackHotelsData.pages[
		fallbackHotelsData.pages.length - 1
	].hotels : [...hotels, ...(similarData?.hotels || [])];
	const emptyFallbackData = shouldRunFallbackQuery && !fallbackHotelsData?.pages[0]?.hotels.length
	const isSimilarHotels = !!similarData && similarData?.hotels?.length > 0;

	// Crutch for a crutch, all of loading states should be refactored
	// Checking if main loading animation is shown
	const suppressSecondaryAnimation = (!isPageLoadingWithData && noHotelsFound && !hasFilters) && (!isPageLoadingWithData && !isPageLoadingWithData && !isLoadingHotels)

	// TODO: Remove or update all data.filters usage

	return (
		<div className={classNames("container mx-auto max-lg:pb-5 rtl:xl:pr-4 rtl:xl:pl-0",
			{ "!max-w-full": !isListView || isFullScreenMap, 'xl:pr-0': isFullScreenMap })}
		>
			<div
				className={classNames('flex', {
					'justify-between': !isSingleHotelRequest && !noHotelsFound,
					'justify-center': isSingleHotelRequest || noHotelsFound,
				})}
			>
				{/* Sidebar */}
				{!isSingleHotelRequest && (!noHotelsFound || hasFilters || isPageLoadingWithData) && (
					<div
						className={classNames(
							'w-[296px] flex-shrink-0 xl:block hidden',
						)}
					>
						{isPageLoadingWithData ? <FiltersPlaceholder isListView={isListView} isFullMap={isFullScreenMap} t={t} /> :
							(
								<ResultsFilters
									pageProps={props}
									availableRates={availableRates}
								/>
							)}
					</div>
				)}

				{/* Main content */}
				<div className={classNames("flex-grow w-full xl:max-w-[calc(100%-300px)]", { "xl:max-w-full": !isListView && noHotelsFound })}>
					{!isPageLoadingWithData && noHotelsFound && !hasFilters && isListView && (
						<NoHotelsFound />
					)}
					{!isPageLoadingWithData && isListView && (
						<NoResultsFound isPageLoading={isPageLoadingWithData} />
					)}
					{/* Header */}
					{isPageLoadingWithData && emptyFallbackData && !isListView && <div className='h-[56px]' />}
					<div
						className={classNames(
							'flex flex-col items-start md:gap-4 xl:flex-row-reverse xl:items-center xl:gap-0 justify-between w-full mb-4',
							{ 'xl:hidden': isFullScreenMap /*, 'hidden': isSingleHotelRequest && isListView */ }
						)}
					>
						{/* TODO: Check SortingAndFilters */}
						<SortingAndFilters
							pageProps={props}
							availableRates={{
								min: data.lowestNightlyRate,
								max: data.highestNightlyRate,
							}}
							isFallbackData={shouldRunFallbackQuery}
							isSimilarHotels={isSimilarHotels}
							isSingleHotels={isSingleHotelRequest}
							availableFilters={hasFilters ? normalizedAvailableFilters : (data.filters as FiltersCountMap)}
						/>
						{!emptyFallbackData && !(isSingleHotelRequest && isListView) && <ContentHeader />}
					</div>
					<div className='flex flex-row gap-4 hotels-list-container'>

						{/* Main Hotels List */}
						<div
							className={classNames('hotels-list', {
								'withMap': !isListView && !tabletDown,
								'hidden': isFullScreenMap,
							})}
						>
							{!isPageLoadingWithData && noHotelsFound && !hasFilters && !isListView && (
								<NoHotelsFound />
							)}
							{!isPageLoadingWithData && !isListView && (
								<NoResultsFound isPageLoading={isPageLoadingWithData} />
							)}
							<HotelsList
								lang={props.params.lang}
								pageParams={props}
								showLoadMore={hasNextPage && !isSingleHotelRequest}
								shouldLoadMore={!isSingleHotelRequest}
								data={data}
								setHoveredItem={setHoveredItem}
								fetchNextPage={fetchNextPage}
								isPageLoading={isPageLoadingWithData}
								isLoadingHotels={isLoadingHotels}
								hasNextPage={hasNextPage}
								isFetchingNextPage={isFetchingNextPage}
								allData={hotelsData}
								totalPages={data.pages}
								isSingleHotelRequest={isSingleHotelRequest}
								isLandmark={isLandmarkSearch}
								isComplete={isFetchingComplete}
							/>
							{!!similarData && similarData?.hotels?.length > 0 && (
								<>
									{isListView ? <div className="md:mt-8"><ContentHeader /></div> : (
										<h2 className="font-bold text-sm lg:text-lg text-text-900 mt-6 mb-4">
											{t('hotelsSimilarTo')}
											<span className="mx-1">
												{decodeURIComponent(hotel)}
											</span>
										</h2>
									)}
									<HotelsList
										lang={props.params.lang}
										pageParams={props}
										data={similarData}
										setHoveredItem={setHoveredItem}
										fetchNextPage={fetchNextSimilarPage}
										hasNextPage={!!hasNextSimilarPage}
										isFetchingNextPage={isFetchingNextSimilarPage}
										allData={similarHotelsData}
										showLoadMore={!!hasNextSimilarPage}
										shouldLoadMore
										totalPages={similarData.pages}
										isSimilarData
										isComplete={isSimilarFetchingComplete}
										isSingleHotelRequest
										isLandmark={isLandmarkSearch}
										suppressLoadingAnimation={suppressSecondaryAnimation}
									/>
								</>
							)}
							{noHotelsFound && !!fallbackHotelsData?.pages && !!fallbackHotelsData?.pages[0]?.hotels.length && (
								<>
									{!isListView && <h2 className="font-bold text-sm lg:text-lg mt-8 mb-4 text-text-900 truncate">
										{t('dealsThatYouMightLike')}
									</h2>}
									<HotelsList
										lang={props.params.lang}
										pageParams={props}
										showLoadMore={!hotel}
										shouldLoadMore={!hotel}
										isComplete={isFallbackFetchingComplete}
										isFallbackData
										data={
											fallbackHotelsData.pages[
											fallbackHotelsData.pages.length - 1
											]
										}
										fetchNextPage={fetchNextFallbackPage}
										hasNextPage={hasNextFallbackPage}
										isFetchingNextPage={isFetchingNextFallbackPage}
										allData={fallbackHotelsData}
										totalPages={
											fallbackHotelsData.pages[
												fallbackHotelsData.pages.length - 1
											].pages
										}
										setHoveredItem={setHoveredItem}
										isSingleHotelRequest={isSingleHotelRequest}
										isLandmark={isLandmarkSearch}
										suppressLoadingAnimation={suppressSecondaryAnimation}
									/>
								</>

							)}
							{!isListView && !isFullScreenMap && (
								<HotelsPagination
									onPageClick={(pageNumber: number) => {
										// Navigate by updating the page query param to trigger a fresh query without infinite loading
										const current = new URLSearchParams(window.location.search);
										if (pageNumber <= 1) {
											current.delete('page');
										} else {
											current.set('page', String(pageNumber));
										}
										const newQuery = current.toString();
										const newUrl = newQuery ? `${window.location.pathname}?${newQuery}` : window.location.pathname;
										router.push(newUrl);
									}}
									totalPages={data.pages}
									currentPage={data.page || 1}
									totalResults={data.totalResults}
								/>
							)}
						</div>

						{(shouldShowMap && ((!!mapCenter?.lat && !isSingleHotelRequest) || (isSingleHotelRequest && data.hotels[0]))) && (
							<HotelsListMap
								hotels={mapHotelsArray}
								lang={props.params.lang}
								mapCenter={mapCenter}
								onMapChange={setMapQueryKey}
								isCity={!!city}
								isLandmark={isLandmarkSearch}
								hoveredItem={hoveredItem}
								singleHotelResponse={isSingleHotelRequest && data.hotels[0] ? data.hotels[0] : null}
								isLoading={isFetchingNextPage || isLoadingHotels || !isFetchingComplete}
								isFallbackData={noHotelsFound}
								searchParams={data.searchParams}
								fullMapView={isFullScreenMap}
							/>
						)}
						<Button
							name="mapView"
							textColor="white"
							fontSize="xl"
							onClick={handleChangeListViewLG}
							bold
							className={classNames("view-map-button")}
						>
							{isFullScreenMap ? <Icons.list className='[&_path]:stroke-white' /> : <Icons.map />}
							{isFullScreenMap ? t('list') : t('map')}
						</Button>
					</div>
				</div>
			</div>
		</div>
	)
}

export default Hotels
